import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-63afc086"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  class: "q-pa-md q-card",
  style: {"width":"950px","max-width":"95vw"}
}
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h3", null, "Contact Persons", -1))

import { ref, PropType, watch } from 'vue'
import { AgreementContactPerson } from '@/modules/agreements/types'
import ContactList from '@/components/_shared/form-fields/contact-info-list.vue'
import useDialogPluginComponent from 'quasar/src/composables/use-dialog-plugin-component.js';import useQuasar from 'quasar/src/composables/use-quasar.js';
import { useAgreementModule } from '@/modules/agreements'

export default /*#__PURE__*/_defineComponent({
  props: {
    agreementId: {
        type: String,
        required: true
    },
    contacts: {
        type: Array as PropType<AgreementContactPerson[]>,
        required: true
    },
    contactTypes: {
        type: Array as PropType<string[]>,
        required: true
    },
    updateCallback: {
        type: Function as PropType<(t: AgreementContactPerson[]) => void>,
        required: true
    }
},
  emits: ['save', 'cancel'],
  setup(__props, { emit }) {

const props = __props

const { createAgreementContactInfo, updateAgreementContactInfo, deleteAgreementContactInfo } = useAgreementModule()

// Props for initial contacts and types



const { dialogRef, onDialogOK, onDialogCancel } = useDialogPluginComponent()
const $q = useQuasar()
const isDisabled = ref(true)
const enableButton = () => {
    isDisabled.value = false
}
// Reactive copy of contacts
const refContactList = ref(null)

// Function to ensure emailIdentifier exists
function generateEmailIdentifier (contact: AgreementContactPerson) {
    return contact.email.trim().toLowerCase()
}

function preprocessContacts (contacts: AgreementContactPerson[]) {
    return contacts.map(contact => ({
        ...contact,
        emailIdentifier: contact.emailIdentifier || generateEmailIdentifier(contact)
    }))
}

// Reactive copy of contacts with emailIdentifier ensured
const localContacts = ref(preprocessContacts(props.contacts))

// Update localContacts when props.contacts changes
watch(() => props.contacts, (newContacts) => {
    localContacts.value = preprocessContacts(newContacts)
}, { immediate: true, deep: true })

const createContactCallback = async (contact: AgreementContactPerson) => {
    await createAgreementContactInfo(props.agreementId, contact)
}

const updateContactCallback = async (contact: AgreementContactPerson) => {
    await updateAgreementContactInfo(props.agreementId, contact)
}

const deleteContactCallback = async (contactEmail: string) => {
    await deleteAgreementContactInfo(props.agreementId, { email: contactEmail })
}

// Add a new contact
function onAddContact (newContact: AgreementContactPerson) {
    localContacts.value.push(newContact)
    enableButton()
}

// Update a contact based on emitted event from contact-info-list
function onUpdateContact (updatedContact: AgreementContactPerson) {
    const index = localContacts.value.findIndex(c => c.emailIdentifier === updatedContact.emailIdentifier)
    if (index !== -1) {
        if (refContactList.value.isNewContact(updatedContact)) {
            updatedContact.emailIdentifier = generateEmailIdentifier(updatedContact)
        }
        localContacts.value[index] = updatedContact
        enableButton()
    }
}

// Delete a contact
function onDeleteContact (contactToDelete: AgreementContactPerson) {
    localContacts.value = localContacts.value.filter(c => c.emailIdentifier !== contactToDelete.emailIdentifier)
    enableButton()
}

function isContactModified (contact: AgreementContactPerson, original: AgreementContactPerson) {
    return (
        contact.name !== original.name ||
        contact.email !== original.email ||
        JSON.stringify(contact.types) !== JSON.stringify(original.types)
    )
}

function isListInValid (): boolean {
    return !refContactList.value.validateList()
}

// Save and close the dialog
async function onSave () {
    if (isListInValid()) return

    const errorMessages: string[] = []

    const displayErrorMessages = (messages: string | string[]): boolean => {
        const messageArray = Array.isArray(messages) ? messages : [messages]
        if (messageArray.length === 0) return false
        messageArray.forEach(message => {
            $q.notify({
                color: 'negative',
                message: message
            })
        })
        return true
    }

    const errorHandler = (err: Error) => {
        errorMessages.push(err.message)
    }

    try {
        const originalContacts = new Map(
            props.contacts.map(contact => [contact.email, contact])
        )

        for (const contact of localContacts.value) {
            const originalContact = originalContacts.get(contact.emailIdentifier)

            if (!originalContact) {
                await createContactCallback(contact).catch(errorHandler)
            } else if (isContactModified(contact, originalContact)) {
                await updateContactCallback(contact).catch(errorHandler)
            }

            // Remove processed contacts from the original list
            originalContacts.delete(contact.emailIdentifier)
        }

        // Remaining contacts in originalContacts are deleted
        for (const remainingContact of originalContacts.values()) {
            await deleteContactCallback(remainingContact.email).catch(errorHandler)
        }

        if (displayErrorMessages(errorMessages)) {
            return // Do not close the dialog if there are errors
        }

        emit('save', localContacts.value)
        onDialogOK()
        props.updateCallback([...localContacts.value.map(contact => ({ ...contact }))])
    } catch (err) {
        if (err instanceof Error) {
            displayErrorMessages(err.message)
        } else {
            displayErrorMessages('Unexpected error occurred while saving agreement contacts')
        }
    }
}

// Cancel changes and close the dialog
function onCancel () {
    localContacts.value = [...props.contacts] // Reset to initial contacts
    emit('cancel')
    onDialogCancel()
}

return (_ctx: any,_cache: any) => {
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_card_actions = _resolveComponent("q-card-actions")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_dialog = _resolveComponent("q-dialog")!

  return (_openBlock(), _createBlock(_component_q_dialog, {
    ref_key: "dialogRef",
    ref: dialogRef,
    onHide: _ctx.onDialogHide
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_component_q_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_q_card_section, null, {
              default: _withCtx(() => [
                _hoisted_2
              ]),
              _: 1
            }),
            _createVNode(_component_q_card_section, null, {
              default: _withCtx(() => [
                _createVNode(ContactList, {
                  contacts: localContacts.value,
                  "contact-types": __props.contactTypes,
                  ref_key: "refContactList",
                  ref: refContactList,
                  onUpdateContact: onUpdateContact,
                  onDeleteContact: onDeleteContact,
                  onAddContact: onAddContact
                }, null, 8, ["contacts", "contact-types"])
              ]),
              _: 1
            }),
            _createVNode(_component_q_card_actions, {
              align: "right",
              class: "q-mt-sm"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_q_btn, {
                  onClick: onSave,
                  label: "Save",
                  color: "primary",
                  "no-caps": "",
                  unelevated: "",
                  disable: isDisabled.value
                }, null, 8, ["disable"]),
                _createVNode(_component_q_btn, {
                  onClick: onCancel,
                  color: "primary",
                  label: "Cancel",
                  outline: "",
                  "no-caps": ""
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ])
    ]),
    _: 1
  }, 8, ["onHide"]))
}
}

})